import type { DepartmentId, WorkAuthority } from "./agentExecutionContract";

export type AgentLevel = "CHIEF" | "LEAD" | "SPECIALIST" | "VERIFIER";

export interface AgentDefinition {
  readonly id: string;

  readonly department: DepartmentId;

  readonly level: AgentLevel;

  readonly role: string;

  readonly reportsTo?: string;

  readonly capabilities: readonly string[];

  readonly authorities: readonly WorkAuthority[];

  readonly maxConcurrentWork: number;

  readonly verificationIndependent: boolean;
}

export class AgentRegistry {
  private readonly agents = new Map<string, AgentDefinition>();

  register(agent: AgentDefinition) {
    if (this.agents.has(agent.id)) {
      throw new Error(`Agent ${agent.id} already registered.`);
    }

    if (agent.reportsTo && !this.agents.has(agent.reportsTo)) {
      throw new Error(
        `Agent ${agent.id} reports to unknown agent ${agent.reportsTo}.`,
      );
    }

    this.agents.set(agent.id, agent);
  }

  get(agentId: string) {
    return this.agents.get(agentId) ?? null;
  }

  list() {
    return [...this.agents.values()];
  }

  eligible(input: {
    department: DepartmentId;

    requiredAuthorities: readonly WorkAuthority[];

    verificationRequired?: boolean;
  }) {
    return this.list().filter(
      (agent) =>
        agent.department === input.department &&
        (!input.verificationRequired || agent.verificationIndependent) &&
        input.requiredAuthorities.every((authority) =>
          agent.authorities.includes(authority),
        ),
    );
  }
}
